import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as Lo from 'lodash';
import { toastSuccess } from 'motion-components';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { convertFieldToInt } from 'shared-pages/common/form-helpfunc';
import { isRebatteryLiion } from 'shared-pages/common/constants';
import {
  CollectionOrdersContainerInput,
  FormInput,
  gqlAdd,
  gqlGetCollectionPointById,
} from '../graphql';
import { gqlGetCollectorForCollPoint, gqlGetCollPointContainers } from './queries';
import {
  CollectionOrderOriginatedFromStates,
  CollectionOrderRecordedByStates,
} from 'shared-pages/shared/constants';
import { prepareXrbForMutationData } from './xrb-approval-tabs-flow/use-order-xrb';

/** Μετατροπή containers από API σε γραμμές φόρμας αίτησης */
export function mapContainersFromApi(containers: any[] = []) {
  return containers.map((container) => ({
    id: null,
    containersQty: container?.containersQty ?? null,
    containertypeId: container?.containerTypeId ?? null,
    containertype: {
      id: container?.containerTypeId ?? null,
      name: container?.containerTypeName ?? null,
    },
  }));
}

/** Καθορισμός originatedFromId βάσει ρόλου χρήστη */
export function getOriginatedFromId(appStore: any) {
  if (appStore.isCollectionPoint) return CollectionOrderOriginatedFromStates.CollectionPoint;
  if (appStore.isPlatformOwner) return CollectionOrderOriginatedFromStates.PlatformUser;
  if (appStore.isCollector) return CollectionOrderOriginatedFromStates.CollectorUser;
  return null;
}

/** Snapshot σημείου συλλογής για τη φόρμα */
export function buildCollectionPointSnapshot(collectionPoint: any) {
  if (!collectionPoint) return {};
  return {
    id: collectionPoint.id,
    name: collectionPoint.name,
    addressLine1: collectionPoint.addressLine1,
    addressLine2: collectionPoint.addressLine2,
    addressCity: collectionPoint.addressCity,
    addressArea: collectionPoint.addressArea,
    postalCode: collectionPoint.postalCode,
    addressNomos: collectionPoint.addressNomos,
  };
}

type BuildNewRecordBaseOptions = {
  xrbData?: any;
};

/** Βασικά πεδία νέας αίτησης χωρίς enrichment από σημείο συλλογής */
export function buildNewRecordBase(appStore: any, options?: BuildNewRecordBaseOptions) {
  return {
    orderedDate: new Date(),
    plannedCollectionDate: undefined,
    collectionOrdersContainers: [],
    collectionPointId: appStore.isCollectionPoint ? appStore.associatedId : null,
    collectionpoint: {},
    recordedById: CollectionOrderRecordedByStates.WEB,
    originatedFromId: getOriginatedFromId(appStore),
    collectorId: appStore.isCollector ? appStore.associatedId : null,
    ...(isRebatteryLiion && options?.xrbData != null && { xrbData: options.xrbData }),
  };
}

export type CollectionPointEnrichment = {
  collectionPointId: number;
  collectorId: number | undefined;
  contactPerson: string;
  collectionpoint: ReturnType<typeof buildCollectionPointSnapshot>;
  collectionOrdersContainers: ReturnType<typeof mapContainersFromApi>;
};

/** Συγχώνευση enrichment σημείου συλλογής σε εγγραφή αίτησης */
export function mergeCollectionPointIntoNewRecord(
  base: ReturnType<typeof buildNewRecordBase>,
  enrichment: CollectionPointEnrichment,
) {
  return {
    ...base,
    collectionPointId: enrichment.collectionPointId,
    collectionpoint: enrichment.collectionpoint,
    contactPerson: enrichment.contactPerson,
    collectionOrdersContainers: enrichment.collectionOrdersContainers,
    collectorId: isRebatteryLiion ? base.collectorId : enrichment.collectorId ?? base.collectorId,
  };
}

type PrepareMutationOptions = {
  isRebatteryLiion: boolean;
  isNew: boolean;
  idNum: number;
};

/** Προετοιμασία τιμών για GraphQL mutation (add/update) */
export function prepareCollectionOrderMutationValues(
  values: any,
  { isRebatteryLiion: isLiion, isNew, idNum }: PrepareMutationOptions,
) {
  const mutValues: any = Lo.pick(values, Lo.keys(new FormInput()));

  mutValues.collectionOrdersContainers = (mutValues.collectionOrdersContainers ?? [])
    .filter(
      (row: any) =>
        row && (row.id != null || row.containertypeId != null || row.containersQty != null),
    )
    .map((row: any) => {
      const newValues = { ...row };
      convertFieldToInt(newValues, ['containersQty']);
      return Lo.pick(newValues, Lo.keys(new CollectionOrdersContainerInput()));
    });

  prepareXrbForMutationData({ mutValues, isRebatteryLiion: isLiion, isNew, idNum });
  return mutValues;
}

type UseCollectionOrderCreateOptions = {
  getDefaultXrbForNewRecord?: () => any;
};

/**
 * Κοινή λογική δημιουργίας αίτησης συλλογής — reusable από form page και grid.
 */
export function useCollectionOrderCreate(options?: UseCollectionOrderCreateOptions) {
  const { appStore } = useRootStore();
  const { handleGraphqlError } = useHandleError('collection-order-create');
  const { getDefaultXrbForNewRecord } = options ?? {};

  const [addNewRecord] = useMutation(gqlAdd);
  const [getCollectorForCollPoint] = useLazyQuery(gqlGetCollectorForCollPoint, {
    fetchPolicy: 'network-only',
  });
  const [getCollPointContainers] = useLazyQuery(gqlGetCollPointContainers, {
    fetchPolicy: 'network-only',
  });
  const [getCollectionPoint] = useLazyQuery(gqlGetCollectionPointById, {
    fetchPolicy: 'network-only',
  });

  /** Ανάκτηση collector, containers και στοιχείων σημείου συλλογής */
  const fetchCollectionPointEnrichment = useCallback(
    async (collectionPointId: number | string): Promise<CollectionPointEnrichment> => {
      const [collectionPointResult, collectorResult, containersRet] = await Promise.all([
        getCollectionPoint({ variables: { id: collectionPointId } }),
        getCollectorForCollPoint({ variables: { collectionPointId } }),
        getCollPointContainers({ variables: { collectionPointId } }),
      ]);

      const collData = collectionPointResult?.data?.getCollectionpointById;
      const collectorId = collectorResult?.data?.getCollectorForCollectionPoint;
      const containers = containersRet?.data?.getContainersOfCollectionPoint?.data ?? [];

      return {
        collectionPointId: collData?.id ?? Number(collectionPointId),
        collectorId: isRebatteryLiion ? undefined : collectorId,
        contactPerson: collData?.contactPerson ?? '',
        collectionpoint: buildCollectionPointSnapshot(collData),
        collectionOrdersContainers: mapContainersFromApi(containers),
      };
    },
    [getCollectionPoint, getCollectorForCollPoint, getCollPointContainers],
  );

  /** Αρχικοποίηση νέας εγγραφής — προαιρετικά με συγκεκριμένο σημείο συλλογής */
  const buildInitialNewRecord = useCallback(
    async (
      collectionPointId?: number,
      options?: { originatedFromCollectionPoint?: boolean },
    ) => {
      const base = buildNewRecordBase(appStore, {
        xrbData: isRebatteryLiion ? getDefaultXrbForNewRecord?.() : undefined,
      });

      const cpId = collectionPointId ?? base.collectionPointId;
      if (!cpId) return base;

      const enrichment = await fetchCollectionPointEnrichment(cpId);
      const merged = mergeCollectionPointIntoNewRecord(base, enrichment);

      // Μόνο όταν έρχεται από navigation state (όχι στη γρήγορη δημιουργία από grid)
      if (options?.originatedFromCollectionPoint) {
        merged.originatedFromId = CollectionOrderOriginatedFromStates.CollectionPoint;
      }

      return merged;
    },
    [appStore, fetchCollectionPointEnrichment, getDefaultXrbForNewRecord],
  );

  /** Εφαρμογή αλλαγής σημείου συλλογής στα πεδία Formik (όπως onCollectionPointIdChange) */
  const applyCollectionPointToForm = useCallback(
    async (
      collectionPointId: number | string,
      setFieldValue: (field: string, value: any) => void,
    ) => {
      const enrichment = await fetchCollectionPointEnrichment(collectionPointId);

      if (enrichment.collectorId) {
        setFieldValue('collectorId', enrichment.collectorId);
      }
      setFieldValue('contactPerson', enrichment.contactPerson || undefined);
      setFieldValue('collectionpoint.addressLine1', enrichment.collectionpoint.addressLine1);
      setFieldValue('collectionpoint.addressCity', enrichment.collectionpoint.addressCity);
      setFieldValue('collectionpoint.postalCode', enrichment.collectionpoint.postalCode);
      setFieldValue('collectionpoint.addressNomos', enrichment.collectionpoint.addressNomos);
      setFieldValue('collectionOrdersContainers', enrichment.collectionOrdersContainers);

      return enrichment;
    },
    [fetchCollectionPointEnrichment],
  );

  /** Δημιουργία αίτησης μέσω mutation */
  const createCollectionOrder = useCallback(
    async (values: any) => {
      const mutValues = prepareCollectionOrderMutationValues(values, {
        isRebatteryLiion,
        isNew: true,
        idNum: -1,
      });
      const d = await addNewRecord({ variables: { data: mutValues } });
      return d.data[Object.keys(d.data)[0]].id as number;
    },
    [addNewRecord],
  );

  /**
   * Γρήγορη δημιουργία: build + submit σε ένα βήμα (για grid).
   * Επιστρέφει το id της νέας αίτησης ή null σε αποτυχία.
   */
  const quickCreateFromCollectionPoint = useCallback(
    async (collectionPointId: number | string) => {
      try {
        const record = await buildInitialNewRecord(Number(collectionPointId));
        const id = await createCollectionOrder(record);
        toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${id}]`);
        return id;
      } catch (error) {
        handleGraphqlError('mutation-add', error);
        return null;
      }
    },
    [buildInitialNewRecord, createCollectionOrder, handleGraphqlError],
  );

  return {
    buildInitialNewRecord,
    fetchCollectionPointEnrichment,
    applyCollectionPointToForm,
    createCollectionOrder,
    quickCreateFromCollectionPoint,
    prepareCollectionOrderMutationValues,
  };
}
